import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import { Plus, Search } from "lucide-react";
import { FILTER_TYPE_NAMES } from "../../types/experiment";

interface FilterPickerProps {
  onSelect: (filterType: string) => void;
}

const CATEGORY_MAP: Record<string, string> = {
  client_version: "Client",
  client_os: "Client",
  client_locale: "Client",
  client_location: "Client",
  client_ip: "Client",
  client_release_channel: "Client",
  client_system_locale: "Client",
  installation_ids: "Client",
  staff: "User",
  user_in_guild: "User",
  user_ids: "User",
  user_locale: "User",
  user_age_range: "User",
  user_id_range: "User",
  user_has_flag: "User",
  user_location: "User",
  user_ip: "User",
  user_premium_type: "User",
  bot: "User",
  guild_ids: "Guild",
  guild_id_range: "Guild",
  guild_member_count_range: "Guild",
  guild_has_feature: "Guild",
  always: "Utility",
  unit_id_in_experiment: "Utility",
  unit_id_in_range_by_hash: "Utility",
  unit_id_matches_filter_snapshot: "Utility",
};

const CATEGORY_COLORS: Record<string, string> = {
  Client: "text-blue",
  User: "text-green",
  Guild: "text-peach",
  Utility: "text-overlay1",
};

export function FilterPicker({ onSelect }: FilterPickerProps) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  const filtered = useMemo(() => {
    const entries = Object.entries(FILTER_TYPE_NAMES);
    if (!search) return entries;
    const q = search.toLowerCase();
    return entries.filter(([, name]) => name.toLowerCase().includes(q));
  }, [search]);

  const grouped = useMemo(() => {
    const groups: Record<string, [string, string][]> = {};
    for (const entry of filtered) {
      const cat = CATEGORY_MAP[entry[0]] ?? "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(entry);
    }
    return groups;
  }, [filtered]);

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm">
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add Filter
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md p-0">
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-sm">Add Filter</DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-overlay0" />
            <Input
              placeholder="Search filters..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 text-xs"
              autoFocus
            />
          </div>
        </div>
        <ScrollArea className="h-72 px-2 pb-2">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category} className="mb-3">
              <div className="flex items-center gap-2 px-2 mb-1">
                <span className={`text-[10px] font-semibold uppercase tracking-wider ${CATEGORY_COLORS[category] ?? "text-overlay1"}`}>
                  {category}
                </span>
                <div className="flex-1 h-px bg-surface1" />
              </div>
              <div className="space-y-0.5">
                {items.map(([key, name]) => (
                  <button
                    key={key}
                    type="button"
                    className="w-full text-left px-3 py-1.5 text-xs rounded-md hover:bg-surface0 transition-colors flex items-center justify-between group"
                    onClick={() => {
                      onSelect(key);
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <span className="text-subtext0 group-hover:text-text transition-colors">{name}</span>
                    <Plus className="h-3 w-3 text-overlay0 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-8 text-xs text-overlay0">
              No filters match "{search}"
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
